import { Typography, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useStore } from '../../Stores'
import { stripHtml } from '../../Helpers/stripHtml'
import { renderRichMessage } from '../../Helpers/richMessage'
import { Message } from '../../Contracts'

const { Text } = Typography

interface TextMessageProps {
  message: Message
}

export function HighlightMatches({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ background: 'var(--ant-color-warning-border)', color: 'var(--ant-color-text)', padding: 0, borderRadius: 2 }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

const TextMessage = observer(({ message }: TextMessageProps) => {
  const { uiStore } = useStore()
  const { token } = theme.useToken()
  const query = uiStore.isMessageSearchOpen ? uiStore.messageSearchQuery : ''
  // Розбір HTML — раз на текст повідомлення, а не на кожен рендер стрічки
  const rich = useMemo(() => renderRichMessage(message.content), [message.content])

  // Search mode: render plain text with highlights
  if (query) {
    return (
      <Text style={{ fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <HighlightMatches text={stripHtml(message.content)} query={query} />
      </Text>
    )
  }

  // Check if content has HTML tags (new rich messages)
  const hasHtml = /<[a-z][\s\S]*>/i.test(message.content)

  if (!hasHtml) {
    // Plain text message (backward compat with existing messages)
    return (
      <Text style={{ fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {message.content}
      </Text>
    )
  }

  // Розмітка — лише з білого списку (`renderRichMessage`), без
  // `dangerouslySetInnerHTML`: сирий `content` з API міг нести
  // `<img onerror>` і виконувався в кожного учасника чату.
  // Стилі — у `chat.css`, кольори — змінними теми
  return (
    <div
      className="rich-message-content"
      style={{
        fontSize: 14,
        lineHeight: '20px',
        wordBreak: 'break-word',
        ['--rmc-code-bg' as string]: token.colorFillTertiary,
        ['--rmc-link-color' as string]: token.colorPrimary,
        ['--rmc-mention-bg' as string]: token.colorPrimaryBg,
        ['--rmc-mention-color' as string]: token.colorPrimary,
      }}
    >
      {rich}
    </div>
  )
})

export default TextMessage
